import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Divider, Flex, Modal } from 'antd';
import { type ReactNode, useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { Ticket } from '@/model/Ticket/Ticket';
import { TICKET_STATE } from '@/model/Ticket/TicketConstants';

import { TicketComplexitySurveyForm } from './TicketComplexitySurveyForm';
import {
  shouldRequestTicketComplexitySurvey,
  useTicketComplexitySurvey,
} from './useTicketComplexitySurvey';

/**
 * Retorna título y mensaje de confirmación según el estado al que se quiere llevar el ticket.
 */
const getStatusContent = (
  status: TICKET_STATE,
  t: (key: string) => string,
): { title: string; message: ReactNode } => {
  switch (status) {
    case TICKET_STATE.CLOSED:
      return {
        title: t('¿Cerrar el ticket sin resolver?'),
        message: (
          <>
            {t('El ticket pasará a cerrado sin quedar marcado como resuelto')}.{' '}
            {t('Se guardarán los cambios realizados')}
          </>
        ),
      };
    case TICKET_STATE.RESOLVED:
      return {
        title: t('¿Marcar el ticket como resuelto?'),
        message: (
          <>
            {t('El ticket quedará resuelto y se dará por finalizada la gestión')}.{' '}
            {t('Se guardarán los cambios realizados')}
          </>
        ),
      };
    case TICKET_STATE.OPEN:
      // Caso post-reabrir: solo se muestra la encuesta sin confirmación adicional.
      return {
        title: t('Encuesta de complejidad del ticket'),
        message: null,
      };
    default:
      return { title: '', message: '' };
  }
};

interface TicketFinalizeModalProps {
  /** Ticket actual con datos de la encuesta (has_complexity_survey). */
  ticket?: Ticket;
  /** Controla la visibilidad del modal. */
  open: boolean;
  /** Estado destino del ticket (CLOSED, RESOLVED u OPEN para solo-encuesta). */
  targetStatus?: TICKET_STATE;
  /** Callback al confirmar: recibe el estado destino y debe ejecutar el submit del formulario.
   *  Si no se provee, el modal solo envía la encuesta y se cierra (modo solo-encuesta). */
  onConfirm?: (status: TICKET_STATE) => void;
  /** Callback al cancelar/cerrar el modal. */
  onCancel: () => void;
}

const TicketFinalizeModal = (props: TicketFinalizeModalProps) => {
  const { t } = useTranslation();
  const { ticket, open, targetStatus, onConfirm, onCancel } = props;
  const survey = useTicketComplexitySurvey(ticket);
  const [submitting, setSubmitting] = useState(false);
  // Determina si este ticket requiere completar la encuesta antes de finalizar.
  const surveyRequired = shouldRequestTicketComplexitySurvey(ticket);
  // El botón de confirmar se habilita solo si no hay encuesta pendiente o si ya es válida.
  const canSubmit = !surveyRequired || survey.isValid;
  // Contenido textual según el estado destino.
  const content = targetStatus ? getStatusContent(targetStatus, t) : { title: '', message: '' };
  /**
   * Maneja el submit unificado: envía la encuesta (si aplica) y luego ejecuta el cambio de estado.
   * En modo solo-encuesta (sin onConfirm), solo envía la encuesta y cierra.
   */
  const handleConfirm = async () => {
    if (!targetStatus) return;

    setSubmitting(true);
    try {
      // Si la encuesta es requerida, la enviamos primero.
      if (surveyRequired) {
        const surveySaved = await survey.submitSurvey();

        if (!surveySaved) return; // Si falla la encuesta, no cerramos el modal.
      }

      // Ejecutar la acción de cambio de estado si fue proporcionada.
      onConfirm?.(targetStatus);
      survey.resetForm();
      // Si no hay onConfirm (modo solo-encuesta), cerramos manualmente.
      if (!onConfirm) onCancel();
    } finally {
      setSubmitting(false);
    }
  };

  /**
   * Cierra el modal y resetea el formulario de encuesta.
   */
  const handleCancel = () => {
    survey.resetForm();
    onCancel();
  };

  return (
    <Modal
      className="TicketComplexityModal"
      title={
        <Flex align="center" gap={8}>
          <InfoCircleOutlined style={{ color: '#faad14', fontSize: 18 }} />
          {content.title}
        </Flex>
      }
      open={open}
      onCancel={handleCancel}
      width={600}
      footer={
        <Flex justify="end" gap={8}>
          <Button onClick={handleCancel}>{t('Cancelar')}</Button>
          <Button
            type="primary"
            disabled={!canSubmit}
            loading={submitting || survey.submitting}
            onClick={() => void handleConfirm()}>
            {t('Guardar y continuar')}
          </Button>
        </Flex>
      }
      maskClosable={false}
      closable={false}>
      {/* Sección de confirmación (oculta en modo solo-encuesta) */}
      {content.message && (
        <div style={{ marginBottom: surveyRequired ? 0 : 8 }}>{content.message}</div>
      )}

      {/* Sección de encuesta (solo si es requerida) */}
      {surveyRequired && (
        <>
          {content.message && <Divider />}
          <p style={{ fontWeight: 500, marginBottom: 12 }}>
            {t('Complete la encuesta de complejidad para continuar')}
          </p>
          <TicketComplexitySurveyForm survey={survey} />
        </>
      )}
    </Modal>
  );
};

export default TicketFinalizeModal;
